export default function Home() {
  // Unified dark-gray/black tone for all text
  const inkStrong = "#1a1a1a"; // near black
  const inkMid = "#2b2b2b";
  const inkSoft = "#3d3d3d";
  const inkMuted = "#5a5a5a";

  return (
    <div
      className="relative w-full"
      style={{
        fontFamily: "'Inter', system-ui, sans-serif",
        color: inkStrong,
      }}
    >
      {/* ---------- Top Nav ---------- */}
      <header className="relative z-20 flex items-center justify-between px-8 py-6">
        {/* Left: brand */}
        <a href="/" className="cursor-pointer group">
          <span
            className="text-[22px] tracking-[0.55em] font-semibold whitespace-nowrap"
            style={{ fontFamily: "'Inter', sans-serif", color: inkStrong }}
          >
            FERRARI
          </span>
        </a>

        {/* Center: nav links */}
        <nav className="hidden md:flex items-center gap-10 text-sm tracking-wide">
          {["Vehicles", "Performance", "Charging", "Discover", "Shop"].map((item) => (
            <a
              key={item}
              href="#"
              className="cursor-pointer transition-colors whitespace-nowrap"
              style={{ color: inkSoft }}
              onMouseEnter={(e) => (e.currentTarget.style.color = inkStrong)}
              onMouseLeave={(e) => (e.currentTarget.style.color = inkSoft)}
            >
              {item}
            </a>
          ))}
        </nav>

        {/* Right: icons */}
        <div className="flex items-center gap-5">
          <button
            aria-label="Help"
            className="cursor-pointer w-8 h-8 flex items-center justify-center transition-colors"
            style={{ color: inkSoft }}
          >
            <i className="ri-question-line text-lg"></i>
          </button>
          <button
            aria-label="Language"
            className="cursor-pointer w-8 h-8 flex items-center justify-center transition-colors"
            style={{ color: inkSoft }}
          >
            <i className="ri-global-line text-lg"></i>
          </button>
          <button
            aria-label="Account"
            className="cursor-pointer w-8 h-8 flex items-center justify-center transition-colors"
            style={{ color: inkSoft }}
          >
            <i className="ri-user-line text-lg"></i>
          </button>
        </div>
      </header>

      {/* ---------- Hero content: fill remaining viewport ---------- */}
      <main
        className="relative z-10 flex flex-col items-center px-4"
        style={{ height: "calc(100vh - 80px)" }}
      >

        {/* Stat row */}
        <section
          aria-label="Performance stats"
          className="mt-4 md:mt-6 w-full max-w-3xl flex flex-wrap items-start justify-center gap-x-12 md:gap-x-20 gap-y-6"
        >
          <StatItem value="2.9" unit="SEC" label="0-60 MPH" inkStrong={inkStrong} inkMid={inkMid} inkMuted={inkMuted} />
          <StatItem value="211" unit="MPH" label="TOP SPEED" inkStrong={inkStrong} inkMid={inkMid} inkMuted={inkMuted} />
          <StatItem value="710" unit="HP" label="MAX POWER" inkStrong={inkStrong} inkMid={inkMid} inkMuted={inkMuted} />
        </section>

        {/* Flex spacer — pushes big title toward the bottom, car sits in the middle */}
        <div className="flex-1" />

        {/* Big brand title — MINI ELECTRIC style: two lines, thin + bold */}
        <h1
          className="select-none text-center leading-[0.9] whitespace-nowrap flex flex-col items-center"
          style={{
            fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
          }}
        >
          <span
            className="block"
            style={{
              fontWeight: 300,
              letterSpacing: "0.02em",
              fontSize: "clamp(48px, 9.5vw, 128px)",
              color: inkStrong,
            }}
          >
            FERRARI
          </span>
          <span
            className="block -mt-2 md:-mt-3"
            style={{
              fontWeight: 800,
              letterSpacing: "0.01em",
              fontSize: "clamp(48px, 9.5vw, 128px)",
              color: inkStrong,
            }}
          >
            DAYTONA
          </span>
        </h1>

        {/* Subtitle tagline */}
        <div
          className="mt-4 md:mt-5 text-[11px] md:text-xs tracking-[0.42em] font-medium uppercase whitespace-nowrap"
          style={{ color: inkMuted, fontFamily: "'Inter', sans-serif" }}
        >
          Hybrid Hypercar <span className="mx-2 opacity-60">·</span> Since 2019
        </div>

        {/* CTA */}
        <div className="mt-8 md:mt-10 mb-6 md:mb-8">
          <button
            className="cursor-pointer whitespace-nowrap px-14 py-3 rounded-full border text-sm tracking-[0.35em] transition-colors"
            style={{
              borderColor: inkStrong,
              color: inkStrong,
              backgroundColor: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = inkStrong;
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = inkStrong;
            }}
          >
            ORDER NOW
          </button>
        </div>
      </main>

      {/* ---------- Scroll runway: gives room for the car to zoom ---------- */}
      {/* The car is fixed to the viewport, so as the user scrolls through this
          section it grows in place. Keep it visually quiet so the car remains
          the star. */}
      <section
        className="relative z-10 w-full"
        style={{ height: "100vh" }}
        aria-hidden="true"
      />

      {/* ---------- Closing tagline once the zoom completes ---------- */}
      <section className="relative z-10 w-full flex items-center justify-center px-4 py-32">
        <div className="max-w-3xl text-center">
          <div
            className="text-[11px] tracking-[0.5em] uppercase mb-6"
            style={{ color: inkMuted }}
          >
            Engineered in Maranello
          </div>
          <h2
            className="leading-[1.05]"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontWeight: 300,
              letterSpacing: "-0.01em",
              fontSize: "clamp(28px, 4vw, 56px)",
              color: inkStrong,
            }}
          >
            Every curve carries decades of racing DNA —
            <span style={{ fontWeight: 700 }}> forged for the road, tuned for the track.</span>
          </h2>
        </div>
      </section>
    </div>
  );
}

/* ------- Stat item component (matches Tesla-style: big number + tiny unit + tiny label) ------- */
function StatItem({
  value,
  unit,
  label,
  inkStrong,
  inkMid,
  inkMuted,
}: {
  value: string;
  unit: string;
  label: string;
  inkStrong: string;
  inkMid: string;
  inkMuted: string;
}) {
  return (
    <div className="flex flex-col items-start">
      <div className="flex items-baseline gap-2">
        <span
          className="font-light leading-none text-[36px] md:text-[48px]"
          style={{
            fontFamily: "'Inter', sans-serif",
            letterSpacing: "-0.02em",
            color: inkStrong,
          }}
        >
          {value}
        </span>
        <span
          className="text-[10px] md:text-xs tracking-[0.25em] font-medium"
          style={{ color: inkMid }}
        >
          {unit}
        </span>
      </div>
      <div
        className="mt-1.5 text-[10px] md:text-[11px] tracking-[0.32em] font-medium"
        style={{ color: inkMuted }}
      >
        {label}
      </div>
    </div>
  );
}